import { DataWorker } from '@/workers/data.worker';
import { IServiceOptions, ServiceFactory, ServiceMode } from '@posx/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useConfigStore } from '@/stores/config';
import { Auth } from '@/capacitors/services/AuthService';
import { isPlatform } from '@ionic/vue';
import { useEmployeeRoleStore } from '@/stores/employee.role';
import * as Wyo from '@wyocrm/sdk';
import { useWyoSdkStore } from '@xpos/crm/src/stores/data';
import { useDevice } from '@/stores/device.store';
import { PresentationService } from '@/services/presentation.service';
import { getPlatform } from './misc';
import { runHealthCheck } from './health.check';
import { startPowerDiagnostics } from './power.diagnostics';

export class ServicesBuilder {
  private static instance: ServiceFactory;
  public constructor() {
  }
  public static getInstance(): ServiceFactory {
    return this.instance;
  }

  private static getServiceOptions(): IServiceOptions {
    const serviceFactoryOptions = {
      mode: ServiceMode.CLOUD,
      base_url: import.meta.env.VITE_BASE_URL,
      device_uid: secureStorage.getItem('device_uid') as string,
      merchant_uid: secureStorage.getItem('merchant_uid') as string,
      token: secureStorage.getAccessToken(),
      database: {
        name: 'xpos',
        version: 1,
      },
    };
    return serviceFactoryOptions;
  }

  public static async initCoreServices(): Promise<void> {
    if (!localStorage.getItem('cap_sec_token_response')) return;
    if (!secureStorage.getItem('merchant_uid')) return;

    if (getPlatform() === 'mobile-web') {
      localStorage.setItem('platform', getPlatform());
      return;
    }
    // Token refresh reuses the existing instance: just update token, don't rebuild Dexie (outlet switch always full-page reloads)
    if (this.instance) return this.instance.updateToken(secureStorage.getAccessToken());

    ServicesBuilder.instance = new ServiceFactory(this.getServiceOptions(), window);
    const config = useConfigStore();
    await config.startLiveQuery();
    localStorage.setItem('platform', getPlatform());

    // Initialize presentation service after core services
    this.initPresentationService();
    runHealthCheck(); // fire-and-forget health check
    startPowerDiagnostics(); // native-only offline-cause diagnostics (#1709)
  }

  public static async initSignalR(): Promise<void> {
    if (!localStorage.getItem('cap_sec_token_response')) return;
    if (!secureStorage.getItem('merchant_uid')) return;
    await DataWorker.getInstance().createOrUpdateConnection(this.getServiceOptions());
  }

  public static async initWyo(): Promise<void> {
    if (!localStorage.getItem('cap_sec_token_response')) return;
    if (!secureStorage.getItem('vault')) return;
    const vault = secureStorage.getVault();
    if (!vault) return;
    const wyoSdkStore = useWyoSdkStore();
    await wyoSdkStore.initWyoService({
      merchantGuid: vault.crm_account_id, //'cc21bd2a-58ac-4267-8b44-ae4ab0d790b7',
      apiKey: vault.crm_api_key,
      baseURL: import.meta.env.VITE_CRM_PROXY_URL,
      defaultHeaders: {},
    });
  }

  public static initPrintingService(): void {
    if (!localStorage.getItem('cap_sec_token_response')) return;
  }

  public static async initPresentationService(): Promise<void> {
    if (!localStorage.getItem('cap_sec_token_response')) return;

    const service = PresentationService.getInstance();
    await service.init(); // 现在会自动根据配置启用/禁用
  }
}
